import React, { useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Copy, History, Check, Minus, Search } from 'lucide-react';
import { toast } from 'sonner';
import { useTeacherLineLinkList } from '@/hooks/useTeacherLineLinkList';
import { TeacherLineHistoryDialog } from './TeacherLineHistoryDialog';

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}

export const TeacherLineLinkManagerDialog: React.FC<Props> = ({ open, onOpenChange }) => {
  const { data, isLoading, error } = useTeacherLineLinkList();
  const [search, setSearch] = useState('');
  const [onlyUnlinked, setOnlyUnlinked] = useState(false);
  const [historyFor, setHistoryFor] = useState<{ code: string; name: string } | null>(null);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return (data || []).filter(t => {
      if (onlyUnlinked && t.has_line_link) return false;
      if (!s) return true;
      return (
        t.code.toLowerCase().includes(s) ||
        t.name.toLowerCase().includes(s) ||
        (t.nickname || '').toLowerCase().includes(s)
      );
    });
  }, [data, search, onlyUnlinked]);

  const copyLink = async (code: string) => {
    const url = `${window.location.origin}/teacher-line-link?teacher_code=${encodeURIComponent(code)}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('คัดลอกลิงก์แล้ว', { description: url });
    } catch {
      toast.error('คัดลอกไม่สำเร็จ');
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>จัดการการเชื่อม LINE ของครู</DialogTitle>
            <DialogDescription>คัดลอกลิงก์ส่งให้ครูเชื่อม LINE และดูประวัติการเชื่อม</DialogDescription>
          </DialogHeader>

          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
            <div className="relative flex-1 w-full">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="ค้นหา รหัส / ชื่อ / ชื่อเล่น"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8"
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch id="only-unlinked" checked={onlyUnlinked} onCheckedChange={setOnlyUnlinked} />
              <Label htmlFor="only-unlinked" className="text-sm">เฉพาะที่ยังไม่เชื่อม</Label>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto border rounded-lg">
            {isLoading && (
              <div className="p-3 space-y-2">
                {[1,2,3,4].map(i => <Skeleton key={i} className="h-12 w-full" />)}
              </div>
            )}
            {error && (
              <p className="p-4 text-sm text-destructive">โหลดข้อมูลไม่สำเร็จ: {(error as Error).message}</p>
            )}
            {!isLoading && !error && (
              <table className="w-full text-sm">
                <thead className="bg-muted/50 sticky top-0">
                  <tr>
                    <th className="text-left p-2 font-medium">รหัส</th>
                    <th className="text-left p-2 font-medium">ชื่อจริง</th>
                    <th className="text-left p-2 font-medium">ชื่อเล่น</th>
                    <th className="text-center p-2 font-medium">LINE</th>
                    <th className="text-left p-2 font-medium">ชื่อ LINE</th>
                    <th className="text-right p-2 font-medium">การกระทำ</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 && (
                    <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">ไม่พบครู</td></tr>
                  )}
                  {filtered.map((t) => (
                    <tr key={t.id} className="border-t hover:bg-muted/30">
                      <td className="p-2 font-mono text-xs">{t.code}</td>
                      <td className="p-2">{t.name}</td>
                      <td className="p-2 text-muted-foreground">{t.nickname || '-'}</td>
                      <td className="p-2 text-center">
                        {t.has_line_link ? (
                          <Badge variant="default" className="bg-green-600 hover:bg-green-600">
                            <Check className="h-3 w-3" />
                          </Badge>
                        ) : (
                          <Badge variant="secondary"><Minus className="h-3 w-3" /></Badge>
                        )}
                      </td>
                      <td className="p-2 text-muted-foreground">
                        {t.has_line_link ? (t.line_display_name || '-') : '-'}
                      </td>
                      <td className="p-2 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button size="sm" variant="outline" onClick={() => copyLink(t.code)}>
                            <Copy className="h-3.5 w-3.5 mr-1" /> ลิงก์
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setHistoryFor({ code: t.code, name: `${t.name}${t.nickname ? ` (${t.nickname})` : ''}` })}>
                            <History className="h-3.5 w-3.5 mr-1" /> ประวัติ
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="text-xs text-muted-foreground">
            ทั้งหมด {filtered.length} คน {data ? `จาก ${data.length}` : ''}
          </div>
        </DialogContent>
      </Dialog>

      <TeacherLineHistoryDialog
        open={!!historyFor}
        onOpenChange={(v) => { if (!v) setHistoryFor(null); }}
        teacherCode={historyFor?.code || null}
        teacherName={historyFor?.name}
      />
    </>
  );
};
